import AponoQueryLexer from '@antlr/generated-files/AponoQueryLexer';
import AponoQueryParser, {
  AndQueryContext,
  EqualsContext,
  IdFieldEqualsOperatorQueryContext,
  IdFieldInOperatorQueryContext,
  InContext,
  NameFieldEqualsOperatorQueryContext,
  NameFieldInOperatorQueryContext,
  NameFieldSubstringOperatorQueryContext,
  NotInContext,
  PermissionTagFieldContext,
  QueryContext,
  ResourceTagFieldContext,
  SubstringOperatorContext,
  OrQueryContext,
} from '@antlr/generated-files/AponoQueryParser';
import { CharStreams, CommonTokenStream, ErrorListener, ParserRuleContext, ParseTree, Recognizer, Token } from 'antlr4';
import {
  AqlBuilderField,
  AqlBuilderOperator,
  AqlFieldExpression,
  AqlFilterItem,
  isAqlBuilderField,
  isAqlBuilderOperator,
} from './common';

export interface FilterProps {
  field: string;
  operator: string;
  value: string | string[];
}

export class QueryParseError extends Error {
  constructor(
    message: string,
    public readonly cause?: Error,
  ) {
    super(message);
    this.name = 'QueryParseError';
  }
}

class ErrorCollector implements ErrorListener<Token> {
  errors: string[] = [];

  syntaxError(
    _recognizer: Recognizer<Token>,
    _offendingSymbol: Token,
    line: number,
    charPositionInLine: number,
    msg: string,
  ): void {
    this.errors.push(`Syntax error at line ${line}:${charPositionInLine}: ${msg}`);
    return;
  }
}

export function parseQueryToTree(query: string): QueryContext {
  try {
    const chars = CharStreams.fromString(query);
    const lexer = new AponoQueryLexer(chars);
    const tokens = new CommonTokenStream(lexer);
    const parser = new AponoQueryParser(tokens);

    lexer.removeErrorListeners();
    parser.removeErrorListeners();
    const errorCollector = new ErrorCollector();
    parser.addErrorListener(errorCollector);

    const tree = parser.query();

    if (errorCollector.errors.length > 0) {
      throw new QueryParseError(errorCollector.errors.join('\n'));
    }

    const lastToken = tokens.LA(1);
    if (lastToken !== AponoQueryLexer.EOF) {
      throw new QueryParseError('Query was not fully parsed. Extra tokens found.');
    }

    return tree;
  } catch (error) {
    if (error instanceof QueryParseError) {
      throw error;
    }
    throw new QueryParseError('Failed to parse query', error instanceof Error ? error : undefined);
  }
}

type SpecificQueryContext =
  | IdFieldEqualsOperatorQueryContext
  | IdFieldInOperatorQueryContext
  | NameFieldEqualsOperatorQueryContext
  | NameFieldInOperatorQueryContext
  | NameFieldSubstringOperatorQueryContext;

type KeyFieldContext = ResourceTagFieldContext | PermissionTagFieldContext;

export function parseTreeToFilters(tree: QueryContext): AqlFilterItem[] {
  const filters: AqlFilterItem[] = [];

  function parseExpression(context: ParserRuleContext): void {
    if (context instanceof OrQueryContext) {
      handleOrExpression(context);
    } else if (isSpecificQueryContext(context)) {
      addFilterFromContext(context);
    } else {
      for (let i = 0; i < context.getChildCount(); i++) {
        const child = context.getChild(i);
        if (child instanceof AndQueryContext || child instanceof QueryContext) {
          parseExpression(child);
        } else if (isSpecificQueryContext(child)) {
          addFilterFromContext(child);
        }
      }
    }
  }

  function handleOrExpression(context: OrQueryContext) {
    const orExpressions: AqlFieldExpression[] = [];
    let currentField: string | undefined;

    const processAllChildren = (ctx: ParserRuleContext) => {
      for (let i = 0; i < ctx.getChildCount(); i++) {
        const child = ctx.getChild(i);
        if (isSpecificQueryContext(child)) {
          const expression = processQueryContext(child);
          if (expression) {
            if (!currentField) currentField = expression.field;
            if (currentField === expression.field) {
              orExpressions.push(expression.expression);
            }
          }
        } else if (child instanceof ParserRuleContext) {
          processAllChildren(child);
        }
      }
    };

    processAllChildren(context);

    if (currentField && orExpressions.length > 0) {
      filters.push({
        field: currentField as AqlBuilderField,
        orExpressions,
      });
    }
  }

  function processQueryContext(
    context: SpecificQueryContext,
  ): { field: string; expression: AqlFieldExpression } | undefined {
    const fieldContext = context.getChild(0);
    const operatorContext = context.getChild(1);
    const valueContext = context.getChild(2);

    let field = fieldContext.getText();
    let operator = operatorContext.getText();
    let value: string[];
    let fieldKey: string | undefined;

    if (isKeyFieldContext(fieldContext)) {
      field = fieldContext.getChild(0).getText();
      fieldKey = unescapeString(fieldContext.stringValue().getText().slice(1, -1));
      if (!fieldKey) return undefined;
    }

    if (operatorContext instanceof EqualsContext || operatorContext instanceof SubstringOperatorContext) {
      value = [unescapeString(valueContext.getText().slice(1, -1))];
    } else {
      if (operatorContext instanceof InContext) {
        operator = AqlBuilderOperator['='];
      }
      if (operatorContext instanceof NotInContext) {
        operator = AqlBuilderOperator['!='];
      }
      value = splitInOperatorValues(valueContext.getText().slice(1, -1));
    }

    if (!isAqlBuilderField(field) || !isAqlBuilderOperator(operator)) {
      return undefined;
    }

    return {
      field,
      expression: {
        key: fieldKey,
        operator,
        value,
      },
    };
  }

  function isSpecificQueryContext(context: ParseTree): context is SpecificQueryContext {
    return (
      context instanceof IdFieldEqualsOperatorQueryContext ||
      context instanceof IdFieldInOperatorQueryContext ||
      context instanceof NameFieldEqualsOperatorQueryContext ||
      context instanceof NameFieldInOperatorQueryContext ||
      context instanceof NameFieldSubstringOperatorQueryContext
    );
  }

  function isKeyFieldContext(context: ParseTree): context is KeyFieldContext {
    return context instanceof ResourceTagFieldContext || context instanceof PermissionTagFieldContext;
  }

  function unescapeString(str: string): string {
    return str.replace(/\\(.)/g, '$1');
  }

  function splitInOperatorValues(str: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    let escaped = false;

    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      if (escaped) {
        current += char;
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === '"' && !inQuotes) {
        inQuotes = true;
      } else if (char === '"' && inQuotes) {
        inQuotes = false;
      } else if (char === ',' && !inQuotes) {
        result.push(unescapeString(current.trim()));
        current = '';
      } else {
        current += char;
      }
    }

    if (current) {
      result.push(unescapeString(current.trim()));
    }

    return result;
  }

  function addFilterFromContext(context: SpecificQueryContext): void {
    const result = processQueryContext(context);
    if (result) {
      filters.push({
        field: result.field as AqlBuilderField,
        andExpression: result.expression,
      });
    }
  }

  parseExpression(tree);
  return filters;
}

function stringify(value: string) {
  return JSON.stringify(value);
}

function expressionToQuery(field: string, expression: AqlFieldExpression): string {
  let stringifiedField = `${field}`;

  if (expression.key) {
    stringifiedField = `${field}["${expression.key}"]`;
  }

  let operator = expression.operator;
  let value = stringify(expression.value[0]);
  if (expression.value.length > 1) {
    if (operator === AqlBuilderOperator['=']) {
      operator = AqlBuilderOperator.in;
    } else if (operator === AqlBuilderOperator['!=']) {
      operator = AqlBuilderOperator.not_in;
    }

    value = `(${expression.value.map(stringify).join(', ')})`;
  }

  return `${stringifiedField} ${operator} ${value}`;
}

export function filtersToQuery(filters: AqlFilterItem[]): string {
  const queryExpressions: string[] = [];

  filters.forEach(({ field, andExpression, orExpressions: filterOrExpressions }) => {
    if (filterOrExpressions?.length) {
      const orExpressions: string[] = [];
      filterOrExpressions.forEach((expression) => {
        orExpressions.push(expressionToQuery(field, expression));
      });
      queryExpressions.push(`(${orExpressions.join(' OR ')})`);
    } else if (andExpression?.value.length) {
      queryExpressions.push(expressionToQuery(field, andExpression));
    }
  });

  return queryExpressions.join(' AND ');
}

export function queryToFilters(query: string): AqlFilterItem[] {
  try {
    return parseTreeToFilters(parseQueryToTree(query));
  } catch (error) {
    console.error(error);
    return [];
  }
}

export function isQueryValid(query: string): boolean {
  try {
    parseQueryToTree(query);
    return true;
  } catch (error) {
    return false;
  }
}
